/**
 * Signed delivery for gated curriculum media.
 *
 * The `lesson-media` and `infographics` buckets hold paid curriculum content.
 * They are private: objects are only reachable through a short-lived signed URL
 * issued to a caller that passes the storage RLS check (platform access or
 * admin). Lesson rows still store the historical `/object/public/<bucket>/<path>`
 * URLs, so this module translates a stored URL into a signed URL at render time.
 *
 * URLs that do not belong to a gated bucket (external CDNs, data URIs, local
 * assets) are returned unchanged.
 */
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const GATED_BUCKETS = new Set(["lesson-media", "infographics"]);
const SIGNED_TTL_SECONDS = 60 * 60; // 1 hour
const REFRESH_MARGIN_MS = 5 * 60 * 1000; // re-sign 5 minutes before expiry

type CacheEntry = { url: string; expiresAt: number };
const cache = new Map<string, CacheEntry>();

export function parseGatedStorageUrl(
  rawUrl: string | null | undefined,
): { bucket: string; path: string } | null {
  if (!rawUrl) return null;
  const match = rawUrl.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)$/);
  if (!match) return null;
  const bucket = match[1];
  if (!GATED_BUCKETS.has(bucket)) return null;
  const path = decodeURIComponent(match[2].split("?")[0]);
  return { bucket, path };
}

/**
 * Resolves a stored media URL to a signed URL when the object lives in a gated
 * bucket. Falls back to the original URL if signing fails, so a transient
 * storage error degrades to a broken asset rather than a broken lesson.
 */
export async function resolveMediaUrl(
  rawUrl: string | null | undefined,
): Promise<string | null> {
  if (!rawUrl) return null;
  const parsed = parseGatedStorageUrl(rawUrl);
  if (!parsed) return rawUrl;

  const cacheKey = `${parsed.bucket}/${parsed.path}`;
  const cached = cache.get(cacheKey);
  if (cached && cached.expiresAt - REFRESH_MARGIN_MS > Date.now()) {
    return cached.url;
  }

  const { data, error } = await supabase.storage
    .from(parsed.bucket)
    .createSignedUrl(parsed.path, SIGNED_TTL_SECONDS);

  if (error || !data?.signedUrl) {
    console.warn("Signed media URL unavailable", parsed.bucket, error?.message);
    return rawUrl;
  }

  cache.set(cacheKey, {
    url: data.signedUrl,
    expiresAt: Date.now() + SIGNED_TTL_SECONDS * 1000,
  });
  return data.signedUrl;
}

/** Resolves a single media URL for rendering. */
export function useSignedMediaUrl(rawUrl: string | null | undefined): string | null {
  const [resolved, setResolved] = useState<string | null>(
    parseGatedStorageUrl(rawUrl) ? null : rawUrl ?? null,
  );

  useEffect(() => {
    let active = true;
    if (!rawUrl) {
      setResolved(null);
      return;
    }
    if (!parseGatedStorageUrl(rawUrl)) {
      setResolved(rawUrl);
      return;
    }
    setResolved(null);
    void resolveMediaUrl(rawUrl).then((url) => {
      if (active) setResolved(url);
    });
    return () => {
      active = false;
    };
  }, [rawUrl]);

  return resolved;
}

/** Resolves a list of media URLs, returning a stored-URL -> render-URL map. */
export function useSignedMediaUrls(rawUrls: (string | null | undefined)[]): Record<string, string> {
  const key = useMemo(() => rawUrls.filter(Boolean).join("|"), [rawUrls]);
  const [map, setMap] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    const urls = key ? key.split("|") : [];
    if (urls.length === 0) {
      setMap({});
      return;
    }
    void Promise.all(
      urls.map(async (url) => [url, (await resolveMediaUrl(url)) ?? url] as const),
    ).then((entries) => {
      if (active) setMap(Object.fromEntries(entries));
    });
    return () => {
      active = false;
    };
  }, [key]);

  return map;
}
